import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Loader2, Layers } from "lucide-react";
import { toast } from "sonner";

// Reusable module activation matrix.
// facilityId=null => manage organisation-level defaults. Otherwise facility-level overrides.
export function ModuleManager({ orgId, facilityId = null, canEdit = true }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => {
    if (!orgId) return;
    setRows(null);
    const q = `organisation_id=${orgId}${facilityId ? `&facility_id=${facilityId}` : ""}`;
    api.get(`/module-activations?${q}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(load, [orgId, facilityId]);

  const toggle = async (m) => {
    if (!canEdit) return;
    const current = facilityId ? m.effective_status : m.organisation_status;
    const next = current === "active" ? "inactive" : "active";
    setBusy(m.code);
    try {
      await api.put("/module-activations", { organisation_id: orgId, facility_id: facilityId, module_code: m.code, status: next });
      toast.success(`${m.name} ${next === "active" ? "activated" : "deactivated"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (rows === null) return <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div>;

  const categories = [...new Set(rows.map((r) => r.category))];

  return (
    <div className="space-y-6">
      {categories.map((cat) => (
        <div key={cat}>
          <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-2">{cat}</div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {rows.filter((r) => r.category === cat).map((m) => {
              const active = (facilityId ? m.effective_status : m.organisation_status) === "active";
              const overridden = facilityId && m.has_override;
              return (
                <div key={m.code} className={`bg-white border rounded-md p-4 flex items-start gap-3 ${active ? "border-blue-200" : "border-slate-200"}`} data-testid={`module-card-${m.code}`}>
                  <span className={`h-9 w-9 rounded-md grid place-items-center shrink-0 ${active ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-400"}`}><Layers className="h-4 w-4" /></span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5">{m.name}
                      {overridden && <span className="text-[9px] font-mono uppercase text-amber-600 bg-amber-50 border border-amber-200 rounded px-1">override</span>}</div>
                    <div className="text-xs text-slate-400 mt-0.5 line-clamp-2">{m.description}</div>
                    {facilityId && <div className="text-[10px] text-slate-400 mt-1 font-mono">org default: {m.organisation_status}</div>}
                  </div>
                  {busy === m.code ? <Loader2 className="h-4 w-4 animate-spin text-blue-600 mt-1" /> :
                    <Switch checked={active} onCheckedChange={() => toggle(m)} disabled={!canEdit || m.code === "core_platform"} data-testid={`module-toggle-${m.code}`} />}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
